// @flow
import React, { useState, useCallback, useRef } from "react";
import { usePdfHighlightInput } from "hooks";
import FormGroup from "react-bootstrap/lib/FormGroup";
import Button from "react-bootstrap/lib/Button";
import ClearableInput from "@hs/clearable-input";
import Avatar from "components/User/Avatar";
import loc from "i18next";
import styles from "./NoteInput.module.css";
import { ActivityType, ItemNoteType, type ActivityDTO } from "data/types";
import { getClassNames } from "data/utils";

type Props = {
    /** callback when user wants to add note
     * @param {ActivityDTO} activity new note to add
     */
    onEnter: (activity: ActivityDTO) => void,
    /** optional callback when user aborts note
     * @param {SyntheticMouseEvent<*>} e mouse event
     */
    onAbort?: (e: SyntheticMouseEvent<*>) => void,
    /** current userId */
    userId: number,
    /** optional: initial values */
    activity?: ActivityDTO,
};

export const getNoteType = (activity: ActivityDTO): string => {
    switch (true) {
        case activity?.extras?.$type === ItemNoteType.PdfHighlight:
            return ItemNoteType.PdfHighlight.toLowerCase();
        case activity?.parentId > 0:
            return "reply";
        default:
            return "note";
    }
};

const NoteInput = ({
    onEnter,
    onAbort,
    userId,
    className,
    activity = { what: ActivityType.comment, extras: null, text: "" },
    autoFocus = false,
}: Props) => {
    const noteRef = useRef(null);
    const [value, setValue] = useState(activity.text);
    const [noteExtras, setNoteExtras] = useState(activity.extras);

    const handleNoteExtras = useCallback(
        (extras) =>
            setNoteExtras(
                extras != null
                    ? /* istanbul ignore next */ extras
                    : activity.extras
            ),
        [activity.extras]
    );

    const { handleHighlight, handleSelect } = usePdfHighlightInput(
        handleNoteExtras,
        noteRef
    );

    const handleEnter = useCallback(
        (text: string) => {
            onEnter({
                ...activity,
                text,
                extras: noteExtras,
            }).then((result) => {
                /* istanbul ignore else */
                if (result) {
                    handleHighlight(null);
                    setValue("");
                    handleSelect();
                }
            });
        },
        [onEnter, noteExtras, activity, handleHighlight, handleSelect]
    );

    const handleAbort = useCallback(
        (e) => {
            typeof onAbort === "function" && onAbort(e);
            setNoteExtras(activity.extras);
            setValue("");
            handleSelect();
        },
        [onAbort, activity.extras, handleSelect]
    );

    return (
        <div
            className={getClassNames(
                "note-input",
                styles.noteContainer,
                className
            )}
        >
            <FormGroup className={styles.noteWrapper}>
                {userId && <Avatar id={userId} className="round" />}
                <ClearableInput
                    multi
                    maxRows={3}
                    aria-label="note"
                    autoFocus={autoFocus}
                    placeholder={loc.t(
                        `activity:${getNoteType(activity)}.placeholder`
                    )}
                    inputRef={
                        /* istanbul ignore next */ (ref) =>
                            (noteRef.current = ref)
                    }
                    onChange={setValue}
                    onEnter={handleEnter}
                    value={value}
                    className={getClassNames("form-control", styles.noteInput)}
                />
            </FormGroup>
            {((activity.extras == null && noteExtras != null) ||
                activity.text !== "" ||
                activity.extras != null ||
                activity.parentId ||
                value !== activity.text) && (
                <Button bsStyle="link" bsSize="xsmall" onClick={handleAbort}>
                    {loc.t(`activity:${getNoteType(activity)}.abort`)}
                </Button>
            )}
        </div>
    );
};
NoteInput.displayName = "NoteInput";
export default NoteInput;
